import axios from 'axios';
import { getToken, refreshAccessToken, clearToken } from '../auth/tokenService';
import { getWorkspaceId } from '../auth/workspaceService';
import i18n from '../i18n';
import { AI_BUDGET_REACHED, AI_USED_EVENT, aiBudgetMessage, isPaidAiCall } from '../utils/aiUsage';

const API = process.env.REACT_APP_BACKEND_URL;

const buildFilterGroup = (filters = [], groups = [], groupsOperator = 'and') => {
  const mappedGroups = groups.map(g => ({
    operator: g.operator || 'and',
    conditions: g.conditions || [],
    groups: []
  }));

  // When groupsOperator is 'or', wrap groups in a parent group so root
  // conditions stay AND-ed while groups relate with the chosen operator
  const topGroups = mappedGroups.length > 1 && groupsOperator !== 'and'
    ? [{ operator: groupsOperator, conditions: [], groups: mappedGroups }]
    : mappedGroups;

  return { operator: 'and', conditions: filters, groups: topGroups };
};

// Authenticated axios instance — all protected endpoints
export const apiClient = axios.create({
  baseURL: `${API}/api/v1`,
});

apiClient.interceptors.request.use(async (config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // MI-268: backend filters all workspace-scoped queries by this header.
  // Endpoints that don't require a workspace (e.g. /auth/me, /workspaces, /users/me)
  // simply ignore it.
  const workspaceId = getWorkspaceId();
  if (workspaceId) {
    config.headers['X-Workspace-Id'] = String(workspaceId);
  }
  return config;
});

// How long to wait after a call over one of the API's limits (a 429), in the
// reader's language: "Try again in 4 minutes", "Inténtalo de nuevo en 4 minutos".
export const tooManyRequestsMessage = (retryAfter) => {
  const seconds = Math.max(1, Math.ceil(Number(retryAfter) || 1));
  const [unit, count] = seconds < 60 ? ['seconds', seconds]
    : seconds < 3600 ? ['minutes', Math.ceil(seconds / 60)]
      : ['hours', Math.ceil(seconds / 3600)];
  const wait = i18n.t(`common:tooManyRequests.${unit}${count === 1 ? 'One' : 'Other'}`, { count });
  return i18n.t('common:tooManyRequests.message', { wait });
};

// A 401 is now the *expected* outcome every 15 minutes, when the access token
// expires — not necessarily a signed-out user. So try one silent refresh and
// replay the request before concluding the session is gone. `_retried` guards
// against a loop when the refresh itself is what failed.
//
// A request sent with `skipAuthRefresh: true` is one whose 401 is an answer,
// not an expired session (`/auth/pending` says "nobody is registering" that
// way). It is handed back to its caller as is: no refresh, and above all no
// bounce to /login, which would throw a signed-out visitor off the page they
// opened (an invitation, say) before it could say anything.
apiClient.interceptors.response.use(
  (response) => {
    // A paid AI call went through: what shows the AI usage reads it again.
    if (isPaidAiCall(response.config)) window.dispatchEvent(new Event(AI_USED_EVENT));
    return response;
  },
  async (error) => {
    // Over a limit: every screen that shows an error's `detail` then says how
    // long to wait, translated, instead of the API's English. Never retried
    // here, since waiting is the point. Over the AI budget (MI-354) it says
    // which one, and when it resets.
    if (error.response?.status === 429) {
      const data = error.response.data || {};
      const retryAfter = data.details?.retry_after ?? error.response.headers?.['retry-after'];
      const detail = data.details?.type === AI_BUDGET_REACHED
        ? aiBudgetMessage(i18n.t.bind(i18n), data.details)
        : tooManyRequestsMessage(retryAfter);
      error.response.data = { ...data, detail };
      return Promise.reject(error);
    }
    const original = error.config;
    if (error.response?.status !== 401 || !original || original._retried
        || original.skipAuthRefresh) {
      return Promise.reject(error);
    }
    original._retried = true;

    const token = await refreshAccessToken();
    if (token) {
      original.headers.Authorization = `Bearer ${token}`;
      return apiClient(original);
    }

    clearToken();
    if (!window.location.pathname.startsWith('/login')
        && !window.location.pathname.startsWith('/signup')) {
      window.location.href = '/login';
      return new Promise(() => {});
    }
    return Promise.reject(error);
  }
);

// ── Auth ────────────────────────────────────────────────────────────────────
// These four carry the session cookie, which is cross-site in every deployed
// environment, so they need `withCredentials`. Nothing else does.

export const fetchCurrentUser = async () => {
  // Never from the browser's cache. The API sends no Cache-Control, so on a
  // back/forward into the app Chromium served an old copy of this answer,
  // and the user's preferences with it: onboarding restarted at step one.
  // Only fetch can ask for no-store (no-cache/max-age were still served
  // stale), hence the adapter.
  const response = await apiClient.get('/auth/me', {
    adapter: 'fetch',
    fetchOptions: { cache: 'no-store' },
  });
  return response.data;
};

// Asked on every signed-out load, invitation page included, and a 401 is its
// usual answer: no registration is pending. Hence `skipAuthRefresh`.
export const getPendingRegistration = async () => {
  const response = await apiClient.get('/auth/pending', {
    withCredentials: true,
    skipAuthRefresh: true,
  });
  return response.data;
};

export const registerAccount = async () => {
  const response = await apiClient.post('/auth/register', null, { withCredentials: true });
  return response.data;
};

export const logoutSession = async () => {
  await apiClient.post('/auth/logout', null, { withCredentials: true });
};

// MI-282 Rev2: merge-patch the current user's UI preferences (language + alert
// toggles). Backend merges the `alerts` map key-by-key.
export const updateUserPreferences = async (preferences) => {
  const response = await apiClient.put('/users/me/preferences', preferences);
  return response.data;
};

// MI-354: the share of today's and this month's AI budget the caller has used,
// `{ day: { percent, resets_at }, month: {…} }`; a window with no budget is null.
export const getMyAiUsage = async () => {
  const response = await apiClient.get('/users/me/ai-usage');
  return response.data;
};

// ============================================================================
// WORKSPACES (MI-268)
// ============================================================================

export const listWorkspaces = async () => {
  const response = await apiClient.get('/workspaces');
  return response.data;
};

export const getWorkspace = async (workspaceId) => {
  const response = await apiClient.get(`/workspaces/${workspaceId}`);
  return response.data;
};

export const createWorkspace = async (name) => {
  const response = await apiClient.post('/workspaces', { name });
  return response.data;
};

export const updateWorkspace = async (workspaceId, payload) => {
  const response = await apiClient.patch(`/workspaces/${workspaceId}`, payload);
  return response.data;
};

export const deleteWorkspace = async (workspaceId) => {
  const response = await apiClient.delete(`/workspaces/${workspaceId}`);
  return response.data;
};

export const listWorkspaceMembers = async (workspaceId) => {
  const response = await apiClient.get(`/workspaces/${workspaceId}/members`);
  return response.data;
};

export const removeWorkspaceMember = async (workspaceId, userId) => {
  await apiClient.delete(`/workspaces/${workspaceId}/members/${userId}`);
};

export const leaveWorkspace = async (workspaceId) => {
  await apiClient.post(`/workspaces/${workspaceId}/members/leave`);
};

export const createInvitation = async (workspaceId, invitedEmail, workspaceRole) => {
  const response = await apiClient.post(`/workspaces/${workspaceId}/invitations`, {
    invited_email: invitedEmail,
    workspace_role: workspaceRole,
  });
  return response.data;
};

export const listInvitations = async (workspaceId) => {
  const response = await apiClient.get(`/workspaces/${workspaceId}/invitations`);
  return response.data;
};

export const revokeInvitation = async (workspaceId, invitationId) => {
  const response = await apiClient.delete(`/workspaces/${workspaceId}/invitations/${invitationId}`);
  return response.data;
};

export const acceptInvitation = async (token) => {
  const response = await apiClient.post('/invitations/accept', { token });
  return response.data;
};

// Who invited you, to which space and with which role, before anything is
// accepted. No auth: the token is the secret, so it works signed out. The
// shared client is fine for that — the endpoint ignores the bearer header
// when there is one, and answers 200 or 404. `skipAuthRefresh` all the same:
// a 401 from anything in front of it must reach the page as a failed
// preview, not send a signed-out visitor to /login without the invitation.
// Read-only: it never consumes the token.
export const previewInvitation = async (token) => {
  const response = await apiClient.get('/invitations/preview', {
    params: { token },
    skipAuthRefresh: true,
  });
  return response.data;
};

// ============================================================================
// MOVEMENTS
// ============================================================================

export const postMovement = async (movementData) => {
  const response = await apiClient.post('/movements', movementData);
  return response.data;
};

export const parseMovement = async (movementText) => {
  const response = await apiClient.post('/movements/parse', { text: movementText });
  return response.data;
};

// Deprecated: Use parseMovement instead (backward compatibility alias)
export const movementFromText = parseMovement;

export const fetchMovementById = async (movementId) => {
  const response = await apiClient.get(`/movements/${movementId}`);
  return response.data;
};

// The similar movements in two groups (same charge, other visits), with
// their totals and a rhythm or, for a huge group, a summary.
export const getSimilarMovements = async (movementId) => {
  const response = await apiClient.get(`/movements/${movementId}/similar`);
  return response.data;
};

export const updateMovement = async (movementId, movementData) => {
  const response = await apiClient.put(`/movements/${movementId}`, movementData);
  return response.data;
};

export const deleteMovement = async (movementId, dryRun = false) => {
  const response = await apiClient.delete(`/movements/${movementId}`, {
    params: dryRun ? { dry_run: true } : {},
  });
  if (dryRun) return response.data;
};

// A delete that would orphan a bank transaction becomes an "ignore"
// instead — the row stays visible but counts toward nothing. Restore undoes it.
// `reason` is optional (MI-315): why it is being ignored, in the user's words.
export const ignoreMovement = async (movementId, reason) => {
  const text = typeof reason === 'string' ? reason.trim() : '';
  const response = await apiClient.post(
    `/movements/${movementId}/ignore`,
    text ? { reason: text } : undefined,
  );
  return response.data;
};

export const restoreMovement = async (movementId) => {
  const response = await apiClient.post(`/movements/${movementId}/restore`);
  return response.data;
};

/**
 * Search movements with day-based pagination
 */
export const searchMovements = async (filters = [], orderBy = null, start_date = null, end_date = null, days = 31, page = 0, groups = [], groupsOperator = 'and', { signal } = {}) => {
  const requestBody = {
    filter_group: buildFilterGroup(filters, groups, groupsOperator),
    order_by: orderBy || [
      { entity: "Movement", column: "date", sort: "desc" }
    ]
  };

  const params = { days, page };
  if (start_date) params.start_date = start_date;
  if (end_date) params.end_date = end_date;

  const response = await apiClient.post('/movements/search', requestBody, { params, signal });
  return response.data;
};

export const exportMovements = async (filters = [], orderBy = null, start_date = null, end_date = null, groups = [], groupsOperator = 'and', days = 31, page = 0, { rows = null, rowOrderBy = null, rowsOffset = 0 } = {}) => {
  const requestBody = {
    filter_group: buildFilterGroup(filters, groups, groupsOperator),
    order_by: orderBy || [
      { entity: "Movement", column: "date", sort: "desc" }
    ]
  };

  // days/page mirror the list's pagination so the file matches what's on
  // screen. MI-336 — the ungrouped list pages by rows instead, so it sends the
  // row window and its ordering, and the server exports that.
  const params = rows != null
    ? { rows, rows_offset: rowsOffset, ...(rowOrderBy ? { row_order_by: rowOrderBy } : {}) }
    : { days, page };
  if (start_date) params.start_date = start_date;
  if (end_date) params.end_date = end_date;

  const response = await apiClient.post('/movements/export', requestBody, {
    params,
    responseType: 'blob',
  });

  const disposition = response.headers['content-disposition'];
  const filename = disposition?.match(/filename="(.+)"/)?.[1]
    || `movements-${new Date().toISOString().slice(0, 10)}.xlsx`;

  const url = URL.createObjectURL(response.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/**
 * Fetch context-aware filter options based on date range
 */
export const fetchSearchOptions = async (startDate = null, endDate = null) => {
  const params = new URLSearchParams();
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);

  const queryString = params.toString();
  const url = `/movements/filter-options${queryString ? `?${queryString}` : ''}`;

  const response = await apiClient.get(url);
  return response.data;
};

/**
 * Value-first filter suggestions: which values match `query`, and how many
 * movements each would leave once its condition is added. Scoped to the period
 * AND to the filters already applied, so the counts mean what the menu implies.
 */
export const fetchFilterSuggestions = async (
  query,
  { filters = [], groups = [], groupsOperator = 'and', start_date = null, end_date = null, limitPerField = 5, signal } = {}
) => {
  const params = {};
  if (start_date) params.start_date = start_date;
  if (end_date) params.end_date = end_date;

  const response = await apiClient.post(
    '/movements/filter-suggestions',
    {
      filter_group: buildFilterGroup(filters, groups, groupsOperator),
      q: query,
      limit_per_field: limitPerField,
    },
    { params, signal }
  );
  return response.data;
};

/**
 * Turn free text into filter conditions. Fails soft server-side: a failure
 * comes back as status 'failed' with an empty group, never as an error.
 */
export const parseAiFilter = async (query, { start_date = null, end_date = null, signal } = {}) => {
  const params = {};
  if (start_date) params.start_date = start_date;
  if (end_date) params.end_date = end_date;

  const response = await apiClient.post('/movements/filters/parse', { text: query }, { params, signal });
  return response.data;
};

/**
 * Group movements by specified dimensions
 */
export const groupMovements = async (filters = [], dimensions = ['currency', 'type'], start_date = null, end_date = null, groups = [], groupsOperator = 'and', granularity = null, { orderBy = null, limit = null, offset = 0, includeIgnoredGroups = false, signal } = {}) => {
  const requestBody = { filter_group: buildFilterGroup(filters, groups, groupsOperator), dimensions };
  // MI-307: only meaningful alongside the `period` dimension.
  if (granularity && granularity !== 'month') requestBody.granularity = granularity;
  // MI-139: additive. Omitting these keeps the legacy ordering/unlimited
  // behaviour every other caller of this endpoint depends on.
  if (orderBy) requestBody.order_by = orderBy;
  if (limit != null) requestBody.limit = limit;
  if (offset) requestBody.offset = offset;
  // MI-315: for the Movements list only. A group whose movements are all
  // ignored comes back too, with a zero count and zero totals.
  if (includeIgnoredGroups) requestBody.include_ignored_groups = true;

  const params = {};
  if (start_date) params.start_date = start_date;
  if (end_date) params.end_date = end_date;

  const response = await apiClient.post('/movements/group', requestBody, { params, signal });
  return response.data;
};

/**
 * MI-139: one expanded group's window of movement rows.
 *
 * POST /movements/group returns aggregates only — this fills a single group,
 * windowed and ordered independently of every other open group. `cursor` is
 * the opaque `next` token from the previous response; never parse it.
 * Returns { items, total, next }.
 *
 * MI-336 — with `scopes: []` there is no group to narrow to, so the window
 * runs over the whole filtered set: that is the ungrouped Movements list,
 * which is why it can be ordered by amount over everything it shows rather
 * than over whatever happens to be loaded. `withTotal` asks for the COUNT
 * behind "showing 50 of 106"; the grouped view leaves it off because a group
 * header already carries its size.
 */
export const fetchGroupRows = async ({
  filters = [],
  groups = [],
  groupsOperator = 'and',
  scopes = [],
  year = null,
  orderBy = 'amount_desc',
  limit = 5,
  cursor = null,
  start_date = null,
  end_date = null,
  displayCurrency = null,
  withTotal = false,
  signal,
} = {}) => {
  const requestBody = {
    filter_group: buildFilterGroup(filters, groups, groupsOperator),
    // One scope per active level — group, then subgroup. A merged group
    // carries no currency scope on purpose: it spans currencies, and pinning
    // one would hide most of its rows.
    scopes: scopes.map(s => ({ dimension: s.dimension, value: s.value ?? null })),
    order_by: orderBy,
    limit,
  };
  if (withTotal) requestBody.with_total = true;
  if (year != null) requestBody.year = year;
  if (cursor) requestBody.cursor = cursor;
  // MI-318 — with a display currency, an amount ordering ranks by converted
  // value instead of the raw stored amount. Without it the server sorts by the
  // number in the row, which across currencies ranks digit count.
  if (displayCurrency) requestBody.display_currency = displayCurrency;

  const params = {};
  if (start_date) params.start_date = start_date;
  if (end_date) params.end_date = end_date;

  const response = await apiClient.post('/movements/group/rows', requestBody, { params, signal });
  return response.data;
};

/**
 * MI-298: single approximate total of a filtered set, converted to one display
 * currency. Returns { display_currency, total, approximate, num_converted,
 * num_unconvertible, unconvertible[] }. Additive — the per-currency views are
 * unaffected; this is only called when a display currency is selected.
 */
export const getConvertedTotal = async (displayCurrency, filters = [], start_date = null, end_date = null, groups = [], groupsOperator = 'and', { signal, dimensions = null, movementIds = null, includeTotals = true, orderBy = null, limit = null, offset = null } = {}) => {
  const requestBody = { filter_group: buildFilterGroup(filters, groups, groupsOperator) };

  // URLSearchParams so repeated keys stay `dimensions=a&dimensions=b`, which is
  // what FastAPI binds to a List[str]; axios' default `dimensions[]=` would not.
  const params = new URLSearchParams();
  params.append('display_currency', displayCurrency);
  if (start_date) params.append('start_date', start_date);
  if (end_date) params.append('end_date', end_date);
  // When present, the response also carries `buckets` — the same totals split by
  // dimension, converted per movement date. Grand totals are unaffected.
  (dimensions || []).forEach((d) => params.append('dimensions', d));
  // MI-318 — the ids on screen. Their converted values come back in
  // `movements`, so the list can paint each row in the display currency.
  (movementIds || []).forEach((id) => params.append('movement_ids', id));
  // Asking only for row values skips the scan of the whole filtered set, which
  // is what makes expanding a group cheap.
  if (includeTotals === false) params.append('include_totals', 'false');
  if (orderBy) params.append('order_by', orderBy);
  if (limit != null) params.append('limit', limit);
  if (offset != null) params.append('offset', offset);

  const response = await apiClient.post('/movements/converted-total', requestBody, { params, signal });
  return response.data;
};

/**
 * Fetch spending trend (cumulative expenses) for a date range with previous-period comparison.
 */
export const fetchSpendingTrend = async (start_date, end_date, currency, filters = [], groups = [], groupsOperator = 'and', displayCurrency = null) => {
  const response = await apiClient.post('/dashboard/spending-trend', {
    filter_group: buildFilterGroup(filters, groups, groupsOperator),
  }, {
    params: { start_date, end_date, currency, display_currency: displayCurrency },
  });
  return response.data;
};

/**
 * Fetch income vs expenses series for a date range.
 */
export const fetchIncomeVsExpenses = async (start_date, end_date, currency, filters = [], groups = [], groupsOperator = 'and', displayCurrency = null) => {
  const response = await apiClient.post('/dashboard/income-vs-expenses', {
    filter_group: buildFilterGroup(filters, groups, groupsOperator),
  }, {
    params: { start_date, end_date, currency, display_currency: displayCurrency },
  });
  return response.data;
};

export const mergeMovements = async (primaryMovementId, movementIds, dryRun = false) => {
  const response = await apiClient.post(
    '/movements/merge',
    { primary_movement_id: primaryMovementId, movement_ids: movementIds },
    { params: dryRun ? { dry_run: true } : {} }
  );
  if (dryRun) return response.data;
};

export const splitMovement = async (movementId, parts, dryRun = false) => {
  const response = await apiClient.post(
    '/movements/split',
    { movement_id: movementId, parts },
    { params: dryRun ? { dry_run: true } : {} }
  );
  if (dryRun) return response.data;
};

export const createMovementGroup = async ({ name, type, movement_ids }) => {
  const response = await apiClient.post('/movement-groups', { name, type, movement_ids });
  return response.data;
};

export const fetchPopularNotes = async () => {
  const response = await apiClient.get('/movements/popular-notes');
  return response.data;
};

// ============================================================================
// ACCOUNTS
// ============================================================================

// `includeReconciliation` (MI-311) opts into the per-account balance-vs-bank
// status. It runs per account server-side, so it's off by default — only the
// Accounts list renders it; pickers just need names and ids.
// `includePositions` adds a holding's position, a broker's holdings totals, a
// loan's figures and a person's loans totals.
export const fetchAccounts = async (is_active = null, { includeReconciliation = false, includePositions = false } = {}) => {
  const params = is_active !== null ? { is_active } : {};
  if (includeReconciliation) params.include_reconciliation = true;
  if (includePositions) params.include_positions = true;
  const response = await apiClient.get('/accounts', { params });
  return response.data;
};

// `options` (MI-307): { granularity: 'month'|'week'|'day', includeWindowOpening,
// accountIds }. Defaults keep the monthly, whole-history behaviour. When a
// window is requested at a finer grain, pass includeWindowOpening so the
// response carries the balance accumulated *before* the window — without it the
// cumulative line would start from zero instead of the real balance.
// Per-currency spend and debt position for a tag (MI-316). Computed server
// side over ALL the tag's movements — the movement list is paginated, so a
// client-side sum would silently truncate.
export const getTagSummary = async (tagId) => {
  const response = await apiClient.get(`/tags/${tagId}/summary`);
  return response.data;
};

export const getAccountBalanceHistory = async (start_date = null, end_date = null, tag_ids = null, options = {}) => {
  const body = {};
  if (start_date) body.start_date = start_date;
  if (end_date) body.end_date = end_date;
  if (tag_ids && tag_ids.length > 0) body.tag_ids = tag_ids;
  if (options.granularity && options.granularity !== 'month') body.granularity = options.granularity;
  if (options.includeWindowOpening) body.include_window_opening = true;
  if (options.accountIds && options.accountIds.length > 0) body.account_ids = options.accountIds;
  const response = await apiClient.post('/accounts/balance-history', body);
  return response.data;
};

// Returns one row per (investment account, currency). lifetime_roi_pct and
// annual_effective_pct can be null when not meaningful (no principal in,
// or XIRR didn't converge).
export const getInvestmentRoi = async (account_ids = null) => {
  const body = account_ids ? { account_ids } : {};
  const response = await apiClient.post('/accounts/roi', body);
  return response.data;
};

// Every holding's and broker's return in `currency`: since purchase (each buy
// at its own day's rate), or from `start` (ISO day) to today.
export const getHoldingReturns = async (currency, start = null) => {
  const response = await apiClient.post('/accounts/holding-returns', { currency, start });
  return response.data;
};

export const createAccount = async (accountData) => {
  const response = await apiClient.post('/accounts', accountData);
  invalidateReferenceData();
  return response.data;
};

export const updateAccount = async (accountId, accountData) => {
  const response = await apiClient.put(`/accounts/${accountId}`, accountData);
  invalidateReferenceData();
  return response.data;
};

// Holdings: fold one holding into another of the same broker ("Same as…").
// Returns the holding it was folded into.
export const foldHoldingInto = async (holdingId, intoAccountId) => {
  const response = await apiClient.post(`/accounts/${holdingId}/same-as`, { into_account_id: intoAccountId });
  invalidateReferenceData();
  return response.data;
};

// The prices of a holding, or of every holding of a broker.
export const fetchValuations = async (accountId) => {
  const response = await apiClient.get(`/accounts/${accountId}/valuations`);
  return response.data;
};

// How far a broker's holdings stood over their cost on each of `dates`
// (ISO days): [{ date, currency, unrealized_gain }].
export const fetchUnrealizedGains = async (accountId, dates) => {
  const response = await apiClient.post(`/accounts/${accountId}/unrealized-gains`, { dates });
  return response.data;
};

// `prices` is [{ account_id, unit_price }]: one for a holding, one per
// holding when `accountId` is the broker.
export const recordPrices = async (accountId, asOfDate, prices) => {
  const response = await apiClient.post(`/accounts/${accountId}/valuations`, { as_of_date: asOfDate, prices });
  return response.data;
};

export const deleteValuation = async (accountId, valuationId) => {
  await apiClient.delete(`/accounts/${accountId}/valuations/${valuationId}`);
};

// Loans: a new loan to a person (the loan account plus the transfer that
// funds it), and a payment from a person split across their loans.
export const createLoan = async (personId, body) => {
  const response = await apiClient.post(`/accounts/${personId}/loans`, body);
  invalidateReferenceData();
  return response.data;
};

export const recordLoanPayment = async (personId, body) => {
  const response = await apiClient.post(`/accounts/${personId}/payments`, body);
  return response.data;
};

// ============================================================================
// CATEGORIES
// ============================================================================

export const fetchCategories = async () => {
  const response = await apiClient.get('/categories');
  return response.data;
};

export const createCategory = async (categoryData) => {
  const response = await apiClient.post('/categories', categoryData);
  invalidateReferenceData();
  return response.data;
};

export const updateCategory = async (categoryId, categoryData) => {
  const response = await apiClient.put(`/categories/${categoryId}`, categoryData);
  invalidateReferenceData();
  return response.data;
};

export const deleteCategory = async (categoryId, dryRun = false) => {
  const response = await apiClient.delete(`/categories/${categoryId}`, {
    params: dryRun ? { dry_run: true } : {},
  });
  if (dryRun) return response.data;
  invalidateReferenceData();
};

// ============================================================================
// TAGS
// ============================================================================

export const fetchTags = async (is_active = null) => {
  const params = is_active !== null ? { is_active } : {};
  const response = await apiClient.get('/tags', { params });
  return response.data;
};

export const createTag = async (tagData) => {
  const response = await apiClient.post('/tags', tagData);
  invalidateReferenceData();
  return response.data;
};

export const updateTag = async (tagId, tagData) => {
  const response = await apiClient.put(`/tags/${tagId}`, tagData);
  invalidateReferenceData();
  return response.data;
};

export const deleteTag = async (tagId, dryRun = false) => {
  const response = await apiClient.delete(`/tags/${tagId}`, {
    params: dryRun ? { dry_run: true } : {},
  });
  if (dryRun) return response.data;
  invalidateReferenceData();
};

// ============================================================================
// GOALS
// ============================================================================

export const fetchGoals = async (includeArchived = false) => {
  const params = includeArchived ? { include_archived: true } : {};
  const response = await apiClient.get('/goals', { params });
  return response.data;
};

export const createGoal = async (goalData) => {
  const response = await apiClient.post('/goals', goalData);
  return response.data;
};

export const updateGoal = async (goalId, goalData) => {
  const response = await apiClient.put(`/goals/${goalId}`, goalData);
  return response.data;
};

export const deleteGoal = async (goalId) => {
  await apiClient.delete(`/goals/${goalId}`);
};

// Saved, remaining and what each month needs, computed server side over every
// attached movement and converted into the goal's own currency.
export const fetchGoalSummary = async (goalId, currency = null) => {
  const response = await apiClient.get(`/goals/${goalId}/summary`, {
    params: currency ? { currency } : {},
  });
  return response.data;
};

// What this month demands across active goals, in one currency.
export const fetchGoalsRollup = async (currency, period = null) => {
  const params = period ? { currency, period } : { currency };
  const response = await apiClient.get('/goals/rollup', { params });
  return response.data;
};

// Attaches what the attach rules allow and reports the rest, instead of
// failing the batch: { goal_id, attached[], skipped[{movement_id, reason}] }.
export const attachMovementsToGoal = async (goalId, movementIds) => {
  const response = await apiClient.post(`/goals/${goalId}/movements`, { movement_ids: movementIds });
  return response.data;
};

export const detachMovementFromGoal = async (goalId, movementId) => {
  await apiClient.delete(`/goals/${goalId}/movements/${movementId}`);
};

export const setGoalAccounts = async (goalId, accountIds) => {
  const response = await apiClient.put(`/goals/${goalId}/accounts`, { account_ids: accountIds });
  return response.data;
};

// ============================================================================
// CURRENCIES
// ============================================================================

export const fetchCurrencies = async (is_active = null) => {
  const params = is_active !== null ? { is_active } : {};
  const response = await apiClient.get('/currencies', { params });
  return response.data;
};

// Source catalog (MI-304): seeded global institutions + this workspace's custom
// ones. `country` narrows to that country; sources with no countries are global
// and always returned.
export const fetchSources = async (country = null) => {
  const params = country ? { country } : {};
  const response = await apiClient.get('/sources', { params });
  return response.data;
};

export const createSource = async (source) => {
  const response = await apiClient.post('/sources', source);
  return response.data;
};

// ============================================================================
// REFERENCE DATA (cached)
// ============================================================================

// MI-311: accounts / categories / currencies / tags are the option lists every
// picker needs. They used to be re-fetched on each movement opened — four
// requests per click, with /accounts the slowest endpoint we have. They change
// only when the user edits them, so fetch once per workspace and hand out the
// same promise: callers that arrive while a fetch is in flight join it instead
// of starting four more. The create/update/delete helpers above clear it.
let referenceDataCache = { workspaceId: null, promise: null };

// The plain account list is also what every page asks for on its own: the
// sidebar, the onboarding checklist, the Dashboard's net-worth card, the
// Movements list's group headers, the phone's Home. One request per workspace
// serves them all, held and cleared with the rest of the reference data. The
// Accounts page, which shows the reconciliation status and the positions,
// still asks for those itself.
let accountsCache = { workspaceId: null, promise: null };

export const getAccounts = () => {
  const workspaceId = String(getWorkspaceId() ?? '');
  if (accountsCache.promise && accountsCache.workspaceId === workspaceId) {
    return accountsCache.promise;
  }
  const promise = fetchAccounts()
    .then((accounts) => accounts || [])
    .catch((err) => {
      if (accountsCache.promise === promise) accountsCache = { workspaceId: null, promise: null };
      throw err;
    });
  accountsCache = { workspaceId, promise };
  return promise;
};

export const getReferenceData = () => {
  const workspaceId = String(getWorkspaceId() ?? '');
  if (referenceDataCache.promise && referenceDataCache.workspaceId === workspaceId) {
    return referenceDataCache.promise;
  }
  const promise = Promise.all([
    getAccounts(),
    fetchCategories(),
    fetchCurrencies(true),
    fetchTags(),
  ])
    .then(([accounts, categories, currencies, tags]) => ({
      accounts: accounts || [],
      categories: categories || [],
      currencies: currencies || [],
      tags: tags || [],
    }))
    .catch((err) => {
      // Never cache a failure — the next caller should get a fresh attempt.
      if (referenceDataCache.promise === promise) invalidateReferenceData();
      throw err;
    });
  referenceDataCache = { workspaceId, promise };
  return promise;
};

export const invalidateReferenceData = () => {
  referenceDataCache = { workspaceId: null, promise: null };
  accountsCache = { workspaceId: null, promise: null };
};

export const createCurrency = async (currencyData) => {
  const response = await apiClient.post('/currencies', currencyData);
  invalidateReferenceData();
  return response.data;
};

export const updateCurrency = async (currencyId, currencyData) => {
  const response = await apiClient.put(`/currencies/${currencyId}`, currencyData);
  invalidateReferenceData();
  return response.data;
};

export const deleteCurrency = async (currencyId, dryRun = false) => {
  const response = await apiClient.delete(`/currencies/${currencyId}`, {
    params: dryRun ? { dry_run: true } : {},
  });
  if (dryRun) return response.data;
  invalidateReferenceData();
};

// ============================================================================
// SOURCE FILES
// ============================================================================

export const fetchSourceFiles = async () => {
  const response = await apiClient.get('/source-files');
  return response.data;
};

export const fetchSourceFileAccounts = async () => {
  const response = await apiClient.get('/source-files/accounts');
  return response.data;
};

export const deleteSourceFile = async (sourceFileId, dryRun = false) => {
  const response = await apiClient.delete(`/source-files/${sourceFileId}`, {
    params: dryRun ? { dry_run: true } : {},
  });
  if (dryRun) return response.data;
};

export const fetchSourceFilesCoverage = async (accountId = null) => {
  const response = await apiClient.get('/source-files/coverage', {
    params: accountId != null ? { account_id: accountId } : {},
  });
  return response.data;
};

// Per account: its last twelve months of statement coverage and whether it is
// up to date.
export const fetchAccountFreshness = async () => {
  const response = await apiClient.get('/source-files/account-freshness');
  return response.data;
};

// ============================================================================
// REPORTS (outside /api/v1)
// ============================================================================

export async function fetchGuestTokenFromBackend() {
  try {
    const token = localStorage.getItem('accessToken');
    const response = await axios.get(`${API}/reports/generate-guest-token`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = response.data;
    return data.guest_token || data.token || data.access_token || data;
  } catch (error) {
    console.error('Error fetching guest token:', error);
    return null;
  }
}

// ============================================================================
// UNIFIED STATEMENT PROCESSING API (Background Processing with Polling)
// ============================================================================

export const processStatement = async ({
  accountId,
  source,
  file,
  password = null,
  accountIds = null,
  targetStep = 5,
  force = false
}) => {
  const formData = new FormData();
  formData.append('file', file);
  if (password) {
    formData.append('password', password);
  }

  const params = new URLSearchParams();
  if (accountId) {
    params.append('account_id', accountId);
  } else if (source) {
    params.append('source', source);
    if (accountIds && accountIds.length > 0) {
      accountIds.forEach(id => params.append('account_ids', id));
    }
  } else {
    throw new Error('Either accountId or source must be provided');
  }

  if (targetStep) {
    params.append('target_step', targetStep);
  }
  if (force) {
    params.append('force', 'true');
  }

  const response = await apiClient.post(
    `/statements/processes?${params.toString()}`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return response.data;
};

export const getProcessStatus = async (processId) => {
  const response = await apiClient.get(`/statements/processes/${processId}`);
  return response.data;
};

export const cancelProcess = async (processId) => {
  const response = await apiClient.post(`/statements/processes/${processId}/cancel`, {});
  return response.data;
};

export const retryProcess = async (processId) => {
  const response = await apiClient.post(`/statements/processes/${processId}/retry`, {});
  return response.data;
};

export const getStepResults = async (processId, stepNumber) => {
  const response = await apiClient.get(`/statements/processes/${processId}/steps/${stepNumber}/results`);
  return response.data;
};

export const listProcesses = async ({ status, accountIds, pageSize = 100, page = 1 } = {}) => {
  const params = new URLSearchParams();
  if (status) params.append('status', status);
  if (accountIds?.length) {
    accountIds.forEach((id) => params.append('account_ids', String(id)));
  }
  // Backend paginates on page/page_size (NOT limit/offset). Sending the wrong
  // names silently fell back to page_size=20, so only 20 processes loaded and the
  // client-side file search couldn't find anything beyond them.
  params.append('page', String(page));
  params.append('page_size', String(pageSize));

  const response = await apiClient.get(`/statements/processes?${params.toString()}`);
  return response.data;
};

// ============================================================================
// STATEMENT REPAIR (ingestion repair) — Owner and Co-Owner only
// ============================================================================

// The flags of every statement of the workspace, each with its latest check.
// 404 from an API that has no repair routes yet.
export const listStatementRepairFlags = async () => {
  const response = await apiClient.get('/repairs/statements');
  return response.data;
};

// Checks one statement and answers with the run: its item cards and, after a
// re-read (`reread: true`, a vendor call that can take tens of seconds), the
// statement diff. 409 while an import runs.
export const checkStatement = async (sourceFileId, reread) => {
  const response = await apiClient.post(`/repairs/statements/${sourceFileId}/check`, { reread });
  return response.data;
};

// Leaves a statement its check could not settle as it is: it needs no look
// until it changes (imported again, another gap, a new suggestion, a new
// check). Answers with the statement's flags. 409 not_checked,
// decisions_open or nothing_to_review.
export const reviewStatement = async (sourceFileId) => {
  const response = await apiClient.post(`/repairs/statements/${sourceFileId}/review`, {});
  return response.data;
};

export const getRepairRun = async (runId) => {
  const response = await apiClient.get(`/repairs/runs/${runId}`);
  return response.data;
};

export const acceptRepairItem = async (runId, itemId) => {
  const response = await apiClient.post(`/repairs/runs/${runId}/items/${itemId}/accept`, {});
  return response.data;
};

// "Different events": the item is rejected and both movements stay.
export const rejectRepairItem = async (runId, itemId, reason = null) => {
  const response = await apiClient.post(`/repairs/runs/${runId}/items/${itemId}/reject`, { reason });
  return response.data;
};

export const acceptSafeRepairItems = async (runId) => {
  const response = await apiClient.post(`/repairs/runs/${runId}/accept-safe`, {});
  return response.data;
};

// Answers with the run and an `outcome` ({ applied, blocked, failed, stopped }).
export const applyRepairRun = async (runId) => {
  const response = await apiClient.post(`/repairs/runs/${runId}/apply`, {});
  return response.data;
};

// Answers with the undo run (kind 'undo') and its `outcome`.
export const undoRepairRun = async (runId) => {
  const response = await apiClient.post(`/repairs/runs/${runId}/undo`, {});
  return response.data;
};
